import {
  AssignmentOutlined,
  EventBusy,
  FileOpenOutlined,
  Schedule,
  TaskOutlined,
} from '@mui/icons-material';
import {
  Autocomplete,
  AutocompleteChangeReason,
  Button,
  Hidden,
  List,
  ListItem,
  Skeleton,
  TextField,
} from '@mui/material';
import { useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';

import './WorkFlowHeader.scss';
import { WorkFlowSelect } from './WorkFlowSelect';

type Props<T> = {
  scheduled?: number;
  open?: number;
  closed?: number;
  draft?: number;
  canceled?: number;
  options?: string[];
  selectedOption?: string | null;
  searchOptions?: T[];
  status: string;
  isLoading?: boolean;
  selectedSearchOption?: T | null;
  onStatusClick: (status: Status) => void;
  onOptionClick: (option: string) => void;
  onSearchSelect: (option: T | null, reason: AutocompleteChangeReason) => void;
  onClearStatus?: () => void;
  onClearSearchOptions?: () => void;
};

export enum Status {
  Scheduled = 'SCHEDULED',
  Open = 'OPEN',
  Closed = 'CLOSE',
  Draft = 'DRAFT',
  Canceled = 'CANCELLED',
}

export const WorkFlowHeader = <T extends { name: string }>({
  scheduled,
  closed,
  open,
  draft,
  isLoading,
  canceled,
  status: workflowStatus,
  options,
  selectedOption,
  selectedSearchOption,
  searchOptions,
  onStatusClick,
  onOptionClick,
  onSearchSelect,
  onClearStatus,
  onClearSearchOptions,
}: Props<T>) => {
  const { t } = useTranslation();
  const [inputValue, setInputValue] = useState('');

  const getStatusClickHandler = (status: Status) => () => {
    handleStatusClick(status);
  };

  const handleStatusClick = (status: Status) => {
    onStatusClick(status);
    if (status === workflowStatus) {
      onClearStatus?.();
    } else {
      onClearSearchOptions?.();
    }
  };

  const getOptionsClickHandler = (op: string) => () => {
    if (workflowStatus) {
      onClearStatus?.();
    }
    onOptionClick(op);
    setInputValue('');
  };

  return (
    <div className="work-flow-header d-flex flex-column w-100 my-2">
      <Hidden mdUp>
        {isLoading ? (
          <Skeleton
            sx={{ borderRadius: 1 }}
            className="mb-2"
            variant="rectangular"
            width="100%"
            height={56}
          />
        ) : (
          <WorkFlowSelect
            open={open}
            scheduled={scheduled}
            closed={closed}
            draft={draft}
            canceled={canceled}
            status={workflowStatus}
            onClearStatus={onClearStatus}
            onStatusClick={handleStatusClick}
          />
        )}
      </Hidden>
      <Hidden mdDown>
        <div className="wf-btn-container d-flex flex-md-row gap-3">
          {isLoading && (
            <>
              <Skeleton
                sx={{ borderRadius: 1 }}
                variant="rectangular"
                width={180}
                height={73.39}
              />
              <Skeleton
                sx={{ borderRadius: 1 }}
                variant="rectangular"
                width={180}
                height={73.39}
              />
            </>
          )}
          {scheduled && (
            <Button
              className={`wf-btn scheduled ${
                workflowStatus === Status.Scheduled ? 'active' : ''
              }`}
              onClick={getStatusClickHandler(Status.Scheduled)}
            >
              <Schedule fontSize="large" color="inherit" />
              <div className="d-flex flex-column">
                <span className="wf-value">
                  {scheduled.toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                  })}
                </span>
                {t('btn_scheduled')}
              </div>
            </Button>
          )}

          {open && (
            <Button
              className={`wf-btn open ${
                workflowStatus === Status.Open ? 'active' : ''
              }`}
              onClick={getStatusClickHandler(Status.Open)}
            >
              <FileOpenOutlined fontSize="large" color="inherit" />
              <div className="d-flex flex-column">
                <span className="wf-value">
                  {open.toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                  })}
                </span>
                {t('btn_open')}
              </div>
            </Button>
          )}

          {draft && (
            <Button
              className={`wf-btn draft ${
                workflowStatus === Status.Draft ? 'active' : ''
              }`}
              onClick={getStatusClickHandler(Status.Draft)}
            >
              <AssignmentOutlined fontSize="large" color="inherit" />
              <div className="d-flex flex-column">
                <span className="wf-value">
                  {draft.toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                  })}
                </span>
                {t('btn_draft')}
              </div>
            </Button>
          )}

          {closed && (
            <Button
              className={`wf-btn closed ${
                workflowStatus === Status.Closed ? 'active' : ''
              }`}
              onClick={getStatusClickHandler(Status.Closed)}
            >
              <TaskOutlined fontSize="large" color="inherit" />
              <div className="d-flex flex-column">
                <span className="wf-value">
                  {closed.toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                  })}
                </span>
                {t('btn_closed')}
              </div>
            </Button>
          )}

          {canceled && (
            <Button
              className={`wf-btn cancelled ${
                workflowStatus === Status.Canceled ? 'active' : ''
              }`}
              onClick={getStatusClickHandler(Status.Canceled)}
            >
              <EventBusy fontSize="large" color="inherit" />
              <div className="d-flex flex-column">
                <span className="wf-value">
                  {canceled.toLocaleString('en-US', {
                    minimumIntegerDigits: 2,
                    useGrouping: false,
                  })}
                </span>
                {t('btn_canceled')}
              </div>
            </Button>
          )}
        </div>
      </Hidden>
      <div className="d-flex search-container align-items-center gap-3">
        <Dropdown>
          <Dropdown.Toggle className="shadow-none">
            <span className="mx-4">
              {selectedOption || t('label_filter_by')}
            </span>
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <List>
              {options?.map((op) => (
                <Dropdown.Item
                  color="primary"
                  key={op}
                  className={selectedOption === op ? 'active' : ''}
                  onClick={getOptionsClickHandler(op)}
                >
                  {op}
                </Dropdown.Item>
              ))}
            </List>
          </Dropdown.Menu>
        </Dropdown>
        {searchOptions && (
          <Autocomplete
            id="work-flow-autocomplete"
            className="wf-autocomplete flex-grow-1"
            value={selectedSearchOption}
            options={searchOptions}
            color="primary"
            inputValue={inputValue}
            onInputChange={(_, newInputValue) => {
              setInputValue(newInputValue);
            }}
            renderOption={(params, option) => (
              <ListItem key={option.name} color="primary" {...params}>
                {option.name}
              </ListItem>
            )}
            getOptionLabel={(option) => option.name}
            renderInput={(params) => (
              <TextField
                sx={{ background: '#fff' }}
                {...params}
                color="primary"
                label={t('input_search_placeholder')}
              />
            )}
            onChange={(
              _: any,
              newValue: T | null,
              reason: AutocompleteChangeReason,
            ) => {
              onSearchSelect(newValue, reason);
            }}
          />
        )}
      </div>
    </div>
  );
};
